"use client";

/**
 * SlashCommandPopover — floating command palette anchored to the "/"
 * trigger position inside a TipTap editor.
 *
 * Single responsibility: render the filtered list of block-insert items
 * at the position reported by `useSlashCommand`, handle keyboard
 * navigation (Up/Down/Enter/Escape) and clicks, then dispatch the chosen
 * action via the shared `applyBlockInsertAction` resolver.
 *
 * Reuses the same ITEMS source of truth as `BlockInsertMenu`, so adding a
 * new block type updates both entry points (footer "+" and slash) at
 * once. Only `kind: "command"` items are shown — popover items (Link,
 * Emoji) require sub-menus that don't fit the slash UX; users still get
 * those from the footer "+" button.
 *
 * Renders inline (no React Portal) so it works inside Radix Dialog on
 * React 19 — same pattern as SimpleSelect and the refactored
 * BlockInsertMenu in this branch.
 */

import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type RefObject,
} from "react";
import type { Editor } from "@tiptap/react";

import {
  ITEMS,
  applyBlockInsertAction,
  type BlockInsertAction,
  type ItemDescriptor,
} from "./block-insert-menu";
import { useSlashCommand } from "./use-slash-command";

interface SlashCommandPopoverProps {
  editor: Editor | null;
  /** Ref to the positioned element this popover renders inside. Required
   *  when mounted inside an ancestor that breaks `position: fixed` (e.g.
   *  Radix Dialog with translate). */
  containerRef?: RefObject<HTMLElement | null>;
}

interface CommandItem {
  id: string;
  label: string;
  Icon: React.ComponentType<{ className?: string }>;
  action: BlockInsertAction;
}

/** Project the shared ITEMS array down to the subset the slash menu can
 *  handle (command-style items only) and pre-resolve their actions. */
function projectCommandItems(items: ItemDescriptor[]): CommandItem[] {
  return items
    .filter((item): item is Extract<ItemDescriptor, { kind: "command" }> =>
      item.kind === "command",
    )
    .map((item) => ({
      id: item.id,
      label: item.label,
      Icon: item.Icon,
      action: { type: "command" as const, commandId: item.commandId },
    }));
}

const ALL_COMMAND_ITEMS = projectCommandItems(ITEMS);

function filterByQuery(items: CommandItem[], query: string): CommandItem[] {
  if (!query) return items;
  const lower = query.toLowerCase();
  return items.filter((item) => item.label.toLowerCase().includes(lower));
}

function SlashCommandPopoverImpl({
  editor,
  containerRef,
}: SlashCommandPopoverProps) {
  const { open, query, position, consumeTrigger, close } = useSlashCommand(
    editor,
    containerRef,
  );
  const [selectedIndex, setSelectedIndex] = useState(0);

  const filteredItems = useMemo(
    () => filterByQuery(ALL_COMMAND_ITEMS, query),
    [query],
  );

  // Reset selection when the query (and therefore the visible list) changes.
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  // Close if filtering yields nothing — nothing to pick, no point staying.
  useEffect(() => {
    if (open && filteredItems.length === 0) close();
  }, [open, filteredItems.length, close]);

  const handlePick = useCallback(
    (item: CommandItem) => {
      if (!editor) return;
      // Strip the "/query" trigger first so the command lands on a clean
      // selection, then apply.
      consumeTrigger();
      applyBlockInsertAction(item.action, editor);
    },
    [editor, consumeTrigger],
  );

  // Keyboard navigation. Capture phase + stopPropagation so we win over
  // the editor's own keymap (Enter would otherwise insert a paragraph).
  useEffect(() => {
    if (!open) return;

    const handler = (event: KeyboardEvent) => {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        event.stopPropagation();
        setSelectedIndex((prev) => (prev + 1) % filteredItems.length);
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        event.stopPropagation();
        setSelectedIndex(
          (prev) =>
            (prev - 1 + filteredItems.length) % filteredItems.length,
        );
        return;
      }
      if (event.key === "Enter") {
        const item = filteredItems[selectedIndex];
        if (!item) return;
        event.preventDefault();
        event.stopPropagation();
        handlePick(item);
        return;
      }
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        close();
      }
    };

    document.addEventListener("keydown", handler, true);
    return () => document.removeEventListener("keydown", handler, true);
  }, [open, filteredItems, selectedIndex, handlePick, close]);

  if (!open || filteredItems.length === 0) return null;

  // When containerRef is provided we render with `position: absolute` so
  // we anchor to that ancestor (which the consumer must mark as
  // positioned, e.g. `relative`). Without it, fall back to viewport
  // anchoring via `position: fixed`.
  const positionMode = containerRef ? "absolute" : "fixed";

  return (
    <div
      role="listbox"
      aria-label="Insert block"
      className="z-[400] w-64 rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-lg"
      style={{
        position: positionMode,
        top: position.top,
        left: position.left,
      }}
    >
      <div className="flex flex-col">
        {filteredItems.map((item, index) => {
          const selected = index === selectedIndex;
          return (
            <button
              key={item.id}
              type="button"
              role="option"
              aria-selected={selected}
              onMouseDown={(e) => {
                // Prevent the editor from losing focus before the click
                // handler runs (which would dismiss the popover).
                e.preventDefault();
              }}
              onClick={() => handlePick(item)}
              onMouseEnter={() => setSelectedIndex(index)}
              className={`flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm text-foreground transition-colors ${
                selected ? "bg-accent" : "hover:bg-accent"
              }`}
            >
              <item.Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span>{item.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

const SlashCommandPopover = memo(SlashCommandPopoverImpl);
export default SlashCommandPopover;
